import { useState } from 'react';
import { FormatAlignCenter, FormatAlignLeft, FormatAlignRight } from '@mui/icons-material';
import {
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  DefaultStylePanel,
  DefaultToolbar,
  TldrawUiMenuItem,
  TLUiStylePanelProps,
  useEditor,
  useIsToolSelected,
  useTools,
} from '@tldraw/tldraw';

/**
 * Tldraw에서 제공하는 전체 도구 목록
 *
 * 1. 기본 도구
 * - select: 선택 도구
 * - hand: 이동/패닝 도구
 * - eraser: 지우개 도구
 *
 * 2. 도형 도구
 * - rectangle: 사각형
 * - ellipse: 원형
 * - triangle: 삼각형
 * - line: 선
 * - arrow: 화살표
 *
 * 3. 그리기 도구
 * - draw: 자유 곡선
 * - highlight: 형광펜
 *
 * 4. 텍스트 도구
 * - text: 텍스트
 * - note: 스티커 노트
 *
 * 5. 기타 도구
 * - frame: 프레임/그룹화
 * - asset: 이미지/에셋
 */

export function CustomToolbar() {
  const editor = useEditor();
  const tools = useTools();
  const isSelectSelected = useIsToolSelected(tools['select']); // 선택 도구
  const isHandSelected = useIsToolSelected(tools['hand']); // 이동 도구
  const isEraserSelected = useIsToolSelected(tools['eraser']); // 지우개 도구
  const isTextSelected = useIsToolSelected(tools['text']); // 텍스트
  const isFrameSelected = useIsToolSelected(tools['frame']); // 프레임
  const isAssetSelected = useIsToolSelected(tools['asset']); // 에셋

  return (
    <div>
      <DefaultToolbar>
        <TldrawUiMenuItem {...tools['select']} isSelected={isSelectSelected} />
        <TldrawUiMenuItem {...tools['hand']} isSelected={isHandSelected} />
        <TldrawUiMenuItem {...tools['eraser']} isSelected={isEraserSelected} />
        <TldrawUiMenuItem {...tools['text']} isSelected={isTextSelected} />
        <TldrawUiMenuItem {...tools['frame']} isSelected={isFrameSelected} />
        <TldrawUiMenuItem {...tools['asset']} isSelected={isAssetSelected} />
      </DefaultToolbar>
    </div>
  );
}

// export const uiOverrides: TLUiOverrides = {
//   tools(editor, tools) {
//     // Create a tool item in the ui's context.
//     tools.size = {
//       id: 'size',
//       icon: 'size',
//       label: 'size',
//       kbd: 's',
//       onSelect: () => {
//         console.log('ss');

//         editor.setCurrentTool('size');
//       },
//     };
//     return tools;
//   },
// };

// 사용자 정의 색상 선택기
function CustomColorPicker({ onChange }: { onChange: (color: string) => void }) {
  const colors = [
    'black',
    'grey',
    'light-violet',
    'violet',
    'blue',
    'light-blue',
    'yellow',
    'orange',
    'green',
    'light-green',
    'light-red',
    'red',
    'white',
  ];

  return (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
      {colors.map((color) => (
        <button
          key={color}
          style={{
            width: '24px',
            height: '24px',
            backgroundColor: color,
            border: '1px solid #ddd',
            borderRadius: '50%',
            cursor: 'pointer',
          }}
          onClick={() => {
            if (color === 'orange') {
              color = 'white';
            }
            console.log(color);

            onChange(color);
          }}
        />
      ))}
    </div>
  );
}

export function CustomStylePanel(props: TLUiStylePanelProps) {
  const editor = useEditor();

  const [color, setColor] = useState('#000000');
  const [opacity, setOpacity] = useState(100);
  const [fontSize, setFontSize] = useState('16');
  const [alignment, setAlignment] = useState('start');
  const [showColorPicker, setShowColorPicker] = useState(false);

  // const handleColorChange = (newColor: any) => {
  //   setColor(newColor.hex);
  //   editor.getSelectedShapes().forEach((shape) => {
  //     if (shape.type === 'text') {
  //       editor.setStyleForSelectedShapes(DefaultColorStyle, newColor.hex);
  //       // 또는 다음과 같이 시도해볼 수 있습니다
  //       editor.updateShape({
  //         id: shape.id,
  //         type: 'text',
  //         props: {
  //           ...shape.props,
  //           style: {
  //             ...shape.props,
  //             color: newColor.hex,
  //             fill: newColor.hex,
  //           },
  //         },
  //       });
  //     }
  //   });
  // };
  const handleColorChange = (color: string) => {
    console.log('🚀 ~ handleColorChange ~ color:', color);
    const selectedShapes = editor.getSelectedShapes();
    selectedShapes.forEach((shape) => {
      editor.updateShapes([
        {
          id: shape.id,
          type: shape.type,
          props: {
            color, // 사용자 정의 색상 적용
          },
        },
      ]);
    });
  };

  const handleOpacityChange = (event: Event, newValue: number | number[]) => {
    setOpacity(newValue as number);
    editor.getSelectedShapes().forEach((shape) => {
      if (shape.type === 'text') {
        editor.updateShape({
          id: shape.id,
          type: 'text',
          props: {
            ...shape.props,
            opacity: (newValue as number) / 100,
          },
        });
      }
    });
  };

  const handleFontSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = event.target.value;
    setFontSize(newSize);
    editor.getSelectedShapes().forEach((shape) => {
      if (shape.type === 'text') {
        editor.updateShape({
          id: shape.id,
          type: 'text',
          props: {
            ...shape.props,
            style: { ...shape.props, fontSize: `${newSize}px` },
          },
        });
      }
    });
  };

  const handleAlignmentChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    if (newAlignment) {
      setAlignment(newAlignment);
      editor.getSelectedShapes().forEach((shape) => {
        if (shape.type === 'text') {
          editor.updateShape({
            id: shape.id,
            type: 'text',
            props: {
              ...shape.props,
              align: newAlignment,
            },
          });
        }
      });
    }
  };

  return (
    <DefaultToolbar>
      <DefaultStylePanel {...props}>
        {/* Color Picker */}
        {/* <Box>
            <Box
              onClick={() => setShowColorPicker(!showColorPicker)}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: color,
                border: '1px solid #ccc',
                cursor: 'pointer',
              }}
            />
            {showColorPicker && (
              <Box sx={{ position: 'absolute', zIndex: 2 }}>
                <ChromePicker color={color} onChange={handleColorChange} />
              </Box>
            )}
          </Box> */}
        <CustomColorPicker onChange={handleColorChange} />

        {/* Opacity Slider */}
        <Stack>
          <Typography>Opacity</Typography>
          <Slider
            value={opacity}
            onChange={handleOpacityChange}
            valueLabelDisplay="auto"
            min={0}
            max={100}
          />
        </Stack>

        {/* Font Size Input */}
        <TextField
          label="Font Size (px)"
          type="number"
          value={fontSize}
          onChange={handleFontSizeChange}
          size="small"
        />

        {/* Text Alignment */}
        <ToggleButtonGroup
          value={alignment}
          exclusive
          onChange={handleAlignmentChange}
          size="small"
        >
          <ToggleButton value="start">
            <FormatAlignLeft />
          </ToggleButton>
          <ToggleButton value="middle">
            <FormatAlignCenter />
          </ToggleButton>
          <ToggleButton value="end">
            <FormatAlignRight />
          </ToggleButton>
        </ToggleButtonGroup>
      </DefaultStylePanel>
    </DefaultToolbar>
  );
}
